import type { ClientBuilderPoliciesAndRoles } from "api-types/builders/buildersResponses";
import type { BuilderProducts } from "api-types/builders/builderTypes";
import type { ClientBuilder } from "api-types/builders/clientBuilderTypes";
import type {
  ClientBuilderUserHydratedWithRoles,
  ClientBuilderUserPermission,
  ClientBuilderUserWithRoles,
} from "api-types/builderUsers/builderUsersResponses";
import type { ClientFeatureFlagPublicData } from "api-types/featureFlags/featureFlagsResponses";
import type { StudioPrefs } from "api-types/users/usersRequests";
import type { ClientUser, ClientUserHydratedWithRoles } from "api-types/users/usersResponses";
import { WEB_API_BASE_PATH } from "common/constants";
import type { FeatureSet } from "common/constants/features";
import Flags from "common/constants/flags";
import { INTERCOM_APP_ID } from "common/constants/marketing";
import { posthog } from "lib/analytics";
import { clearRumUser, setRumUser } from "lib/datadogRum";
import { getFeatureFlagPublicDataURL } from "lib/endpointHelpers";
import { useFetchNoAuth, useFetchNoHandle, useUser } from "lib/fetcher";
import { useAppRouterParams } from "lib/routeHelpers";
import {
  getCurrentBuilderUser,
  getBuilderUserRoleName,
  getCurrentUserEnabledProducts,
  getUserEnabledFeatures,
  userIsSuperAdmin,
  userHasPermission,
} from "lib/security/shared";
import getUserFullName from "lib/utils/userName";
import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useMemo } from "react";

export interface UserContextState {
  readonly user: ClientUserHydratedWithRoles | null;
  readonly builderUser?: ClientBuilderUserHydratedWithRoles;
  readonly builderUserRoleName?: string;
  readonly builder?: ClientBuilder;
  readonly builderPoliciesAndRoles?: ClientBuilderPoliciesAndRoles;
  readonly enabledFeatures: FeatureSet;
  readonly studioPrefs: StudioPrefs;
  readonly enabledProducts: BuilderProducts;
  readonly isSuperAdmin: boolean;
  readonly hasPermission: (
    permission: ClientBuilderUserPermission,
    context?: {
      userOverride?: Pick<ClientUser, "adminRole" | "tempUserRole">;
      builderUserOverride?: Pick<ClientBuilderUserWithRoles, "id" | "userId" | "roles" | "builderId">;
      resourceId?: string;
    },
  ) => boolean;

  readonly userFetchError?: unknown;
  readonly userLoading?: boolean;

  readonly builderFetchError?: unknown;
  readonly builderLoading?: boolean;
}

const initialUserContextState: UserContextState = {
  user: null,
  isSuperAdmin: false,
  enabledFeatures: {} as FeatureSet,
  enabledProducts: {} as BuilderProducts,
  studioPrefs: { keyboardShortcuts: [] },
  hasPermission: () => false,
};

export const UserContext = createContext<UserContextState>(initialUserContextState);

interface UserContextProviderProps {
  children: ReactNode;
}

export default function UserContextProvider({ children }: UserContextProviderProps) {
  const { builderId } = useAppRouterParams();
  const { user, loading: userLoading, error: userFetchError } = useUser();
  const builderUser = getCurrentBuilderUser(user, builderId);
  const builderUserRoleName = getBuilderUserRoleName(user, builderUser ?? undefined);

  const {
    data: builder,
    loading: builderLoading,
    error: builderFetchError,
  } = useFetchNoHandle<ClientBuilder>(`${WEB_API_BASE_PATH}/builders/${builderId}`, [builderId, user]);

  const {
    data: builderPoliciesAndRoles,
    loading: builderPoliciesLoading,
    error: builderPoliciesFetchError,
  } = useFetchNoHandle<ClientBuilderPoliciesAndRoles>(`${WEB_API_BASE_PATH}/builders/${builderId}/policies-roles`, [
    builderId,
    user,
  ]);

  const { data: featureFlagPublicData } = useFetchNoAuth<ClientFeatureFlagPublicData>(
    getFeatureFlagPublicDataURL({ builderId }),
    [builderId],
  );

  const enabledFeatures = useMemo(
    () => getUserEnabledFeatures(user, builder, featureFlagPublicData?.environmentSettingOverrides),
    [user, builder, featureFlagPublicData?.environmentSettingOverrides],
  );
  const enabledProducts = getCurrentUserEnabledProducts(builder);

  useEffect(() => {
    if (user) {
      if (!posthog._isIdentified()) {
        posthog.identify(user.id, {
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          role: builderUserRoleName,
          environment: Flags.DB_ENV,
        });
      }

      if (user?.id) {
        setRumUser(user.id, {
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          role: builderUserRoleName,
          environment: Flags.DB_ENV,
        });
      }
    } else {
      // logout
      posthog.reset();
      clearRumUser();
    }
  }, [user]);

  // This could be replaced by groups, which is a paid feature.
  useEffect(() => {
    if (posthog._isIdentified()) {
      posthog.setPersonProperties({ builder: builder?.companyName });
    }
  }, [builder]);

  useEffect(() => {
    const loadIntercom = async () => {
      // show release notes, link user/builder records in intercom
      // see docs: https://developers.intercom.com/installing-intercom/web/attributes-objects
      if (user) {
        const { default: Intercom } = await import("@intercom/messenger-js-sdk");

        Intercom({
          alignment: "right",
          horizontal_padding: 20,
          vertical_padding: 20,
          app_id: INTERCOM_APP_ID,
          user_id: user.id ?? undefined,
          companies: builder?.id
            ? [
                {
                  company_id: builder.id,
                  company_name: builder.companyName,
                  website: builder.website,
                },
              ]
            : undefined,
          name: getUserFullName(user),
          email: user.email ?? undefined,
          created_at: new Date(user.createdAt).getTime(),
        });
      }
    };

    if (!Flags.IS_DEV_MODE) {
      void loadIntercom();
    }
  }, [user, builder]);

  const userContext: UserContextState = {
    isSuperAdmin: userIsSuperAdmin(user),
    hasPermission: useCallback(
      (permission, context) =>
        userHasPermission(permission, {
          user: context?.userOverride ?? user,
          builderUser: context?.builderUserOverride ?? builderUser ?? undefined,
          builderPoliciesAndRoles,
          resourceId: context?.resourceId,
        }),
      [user, builderUser, builderPoliciesAndRoles],
    ),
    enabledFeatures,
    enabledProducts,
    studioPrefs: (user?.studioPrefs as StudioPrefs) || { keyboardShortcuts: [] },
    user,
    builderUser: builderUser ?? undefined,
    builderUserRoleName: builderUserRoleName ?? undefined,
    builder,
    builderPoliciesAndRoles,
    userFetchError,
    userLoading,
    builderFetchError: builderFetchError ?? builderPoliciesFetchError,
    builderLoading: builderLoading || builderPoliciesLoading,
  };

  return <UserContext.Provider value={userContext}>{children}</UserContext.Provider>;
}

export function useUserContext() {
  const context = useContext(UserContext);
  const { builderId } = useAppRouterParams();

  const noUser = !context.user && !context.userLoading;
  // On public pages (no user), fetch builder
  const { data: builder } = useFetchNoAuth<ClientBuilder>(`${WEB_API_BASE_PATH}/builders/${builderId}`, [
    builderId,
    noUser,
  ]);

  const { data: featureFlagPublicData } = useFetchNoAuth<ClientFeatureFlagPublicData>(
    getFeatureFlagPublicDataURL({ builderId }),
    [builderId, noUser],
  );

  // Memoize so consumers using `enabledFeatures` as an effect dep don't loop.
  const publicEnabledFeatures = useMemo(
    () =>
      featureFlagPublicData?.enabledFeatures ??
      getUserEnabledFeatures(null, builder, featureFlagPublicData?.environmentSettingOverrides),
    [builder, featureFlagPublicData],
  );

  // On public pages, when UserContext provider is skipped, still return enabledFeatures and builder
  if (noUser) {
    return {
      ...context,
      user: null,
      builder,
      enabledFeatures: publicEnabledFeatures,
    };
  }
  return context;
}
